import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import Select from "react-select";
import { Controller, useForm } from "react-hook-form";
import {
  useAddPromoCodeMutation,
  useUpdatePromoCodeMutation,
} from "../../../store/services/promoCodeService";
import toast from "react-hot-toast";
import moment from "moment";
import { useGetProductsQuery } from "../../../store/services/ProductStore";
import {
  useGetCategoryQuery,
  useGetSubCategoryQuery,
} from "../../../store/services/categoryService";
import { useGetSettingsQuery } from "../../../store/services/settingsService";
import { CATEGORY_TYPE } from "../../../constants/enum";
import { skipToken } from "@reduxjs/toolkit/query";
import { FaTrash } from "react-icons/fa";
import { FaTimes } from "react-icons/fa";

// Helper function for number input validation and styling
const getNumberInputProps = (fieldName, validationRules = {}) => {
  const baseStyle = {
    WebkitAppearance: "none",
    MozAppearance: "textfield",
    appearance: "none",
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = (e.clipboardData || window.clipboardData).getData(
      "text"
    );
    const numericValue = pastedText.replace(/[^0-9.]/g, "");

    // Basic validation for numeric input
    if (numericValue && !/^\d+(\.\d+)?$/.test(numericValue)) {
      toast.error("Invalid numeric value. Please enter a valid number.");
      return;
    }

    // Flat discount validation - check decimal places
    if (fieldName === "discountValue") {
      const parts = numericValue.split(".");
      if (parts.length > 2) {
        toast.error("Invalid number format. Only one decimal point allowed.");
        return;
      }
      if (parts[1] && parts[1].length > 2) {
        toast.error("Flat discount cannot have more than 2 decimal places.");
        return;
      }
      if (parts[0] && parts[0].length > 10) {
        toast.error(
          "Flat discount cannot have more than 10 digits before decimal."
        );
        return;
      }
    }

    // Quantity-specific validation
    if (fieldName.includes("Qty") || fieldName.includes("quantity")) {
      const numValue = parseFloat(numericValue);
      // if (numValue === 0 || numValue < 1) {
      //   toast.error("Quantity must be greater than 0.");
      //   return;
      // }
    }

    // Allow paste if validation passes
    const target = e.target;
    const start = target.selectionStart;
    const end = target.selectionEnd;
    const currentValue = target.value;
    const newValue =
      currentValue.substring(0, start) +
      numericValue +
      currentValue.substring(end);
    target.value = newValue;
    target.setSelectionRange(
      start + numericValue.length,
      start + numericValue.length
    );
  };

  const handleKeyDown = (e) => {
    // Prevent arrow keys for increment/decrement
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
    }
  };

  return {
    style: baseStyle,
    onPaste: handlePaste,
    onKeyDown: handleKeyDown,
    ...validationRules,
  };
};

// Options for promoCodeType
const promoCodeTypeOptions = [
  { value: "Offer", label: "Offer/Global" },
  { value: "Offer_ALL", label: "Offer ALL" },
  { value: "Unique", label: "For specific customer" },
  { value: "Referral", label: "Referral" },
  { value: "FirstOrder", label: "First Order" },
  {
    value: "flatDiscountProductCategory",
    label: "Flat discount on product category",
  },
  {
    value: "flatDiscountProductSubCategory",
    label: "Flat discount on product subcategory",
  },
  { value: "buyXgetY", label: "Buy X, Get Y free OR Buy X, Get X free" },
  {
    value: "mealPlusStoreItem",
    label: "Meal Plan + Store Item Bundles",
  },
  {
    value: "autoGiftOnMealPlan",
    label: "Auto-Add Gift on Meal Plan Purchase",
  },
];
const discountTypeOptions = [
  { value: "Percent", label: "Percent" },
  { value: "Flat", label: "Flat" },
];

const PromoCodesPop = ({ title, modalOpen, setModalOpen, edit, userList }) => {
  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    defaultValues: {
      code: edit?.code || "",
      discountValue: edit?.discountValue || "",
      minimumSpent: edit?.minimumSpent || "",
      maxDiscount: edit?.maxDiscount || "",
      minDiscount: edit?.minDiscount || "",
      limit: edit?.limit || "",
      limitPerUser: edit?.limitPerUser || "",
      promoCodeType: edit?.promoCodeType,
      discountType: edit?.discountType || "Percent",
      // storePromoCodeType: edit?.storePromoCodeType || "",

      description: edit?.description || "",
      startDate: edit?.startDate
        ? moment(edit?.startDate).format("YYYY-MM-DD")
        : "",
      expireDate: edit?.expireDate
        ? moment(edit?.expireDate).format("YYYY-MM-DD")
        : "",
    },
  });
  const isUnique = watch("promoCodeType") === "Unique";
  const selectedPromoFor = watch("promoFor");
  const isProductSpecific = selectedPromoFor === "onlineStore";
  const selectedPromoCodeType = watch("promoCodeType");
  const selectedDiscountType = watch("discountType");
  const showFlatDiscountOnProductCategoryFields =
    selectedPromoCodeType === "flatDiscountProductCategory";
  const showFlatDiscountOnProductSubcategoryFields =
    selectedPromoCodeType === "flatDiscountProductSubCategory";
  const showBuyXGetYFreeFields = selectedPromoCodeType === "buyXgetY";
  const showMealPlanStoreItemBundlesFields =
    selectedPromoCodeType === "mealPlusStoreItem";
  const showAutoAddGiftOnMealPlanPurchaseFields =
    selectedPromoCodeType === "autoGiftOnMealPlan";
  const { data: productsData, isLoading: isProductsLoading } =
    useGetProductsQuery({ limit: 100, pageNo: 1 });
  const productOptions =
    productsData?.data?.map((product) => ({
      value: product._id,
      label: product.name,
    })) || [];

  const { data: categoriesData, isLoading: isCategoriesLoading } =
    useGetCategoryQuery({
      type: CATEGORY_TYPE.PRODUCT,
      limit: 100,
      pageNo: 1,
      orderBy: "createdAt",
    });

  const [selectedCategory, setSelectedCategory] = useState(null);
  const { data: subCategoriesData, isLoading: isSubCategoriesLoading } =
    useGetSubCategoryQuery(
      selectedCategory
        ? {
            type: CATEGORY_TYPE.PRODUCT,
            parentCategory: selectedCategory.value,
            limit: 100,
            orderBy: "createdAt",
            pageNo: 1,
          }
        : skipToken
    );

  const [addPromoCode, { isLoading: isAdding }] = useAddPromoCodeMutation();
  const [updatePromoCode, { isLoading: isUpdating }] =
    useUpdatePromoCodeMutation();

  const { data: settingsData, isLoading: isSettingsLoading } =
    useGetSettingsQuery();
  const mealPlanDurationOptions =
    settingsData?.data?.weekDurationOptions?.map((option) => ({
      value: `${option.value}week`,
      label: option.label,
      enabled: option.enabled,
      discount: option.discount,
    })) || [];

  const mealPlanDurationOptionsWithAll = [
    { value: "ALL", label: "Select All" },
    ...mealPlanDurationOptions,
  ];

  const [bundleProductId, setBundleProductId] = useState("");
  const [bundleProductQty, setBundleProductQty] = useState();
  const [bundleProducts, setBundleProducts] = useState([]);
  const [bundleProductsError, setBundleProductsError] = useState("");
  const [addProductsError, setAddProductsError] = useState({
    product: "",
    quantity: "",
  });
  const handleAddBundleProduct = () => {
    if (!bundleProductId || !bundleProductQty || bundleProductQty < 1) {
      setAddProductsError({
        product: !bundleProductId ? "Product is required" : "",
        quantity: !bundleProductQty ? "Quantity is required" : "",
      });
      return;
    }

    // Prevent duplicate product
    if (bundleProducts.some((item) => item.productId === bundleProductId))
      return;
    setBundleProducts([
      ...bundleProducts,
      { productId: bundleProductId, quantity: bundleProductQty },
    ]);
    setBundleProductId("");
    setBundleProductQty("");
  };

  const handleRemoveBundleProduct = (productId) => {
    setBundleProducts(
      bundleProducts.filter((item) => item.productId !== productId)
    );
  };

  useEffect(() => {
    if (
      modalOpen &&
      edit &&
      edit.mealPlanProductBundles &&
      edit.promoCodeType === "mealPlusStoreItem"
    ) {
      setValue(
        "mealPlanBundleDuration",
        edit.mealPlanProductBundles.duration === 0
          ? "ALL"
          : `${edit.mealPlanProductBundles.duration}week`
      );

      setBundleProducts(
        Array.isArray(edit.mealPlanProductBundles.products)
          ? edit.mealPlanProductBundles.products.map((item) =>
              typeof item === "string"
                ? { productId: item, quantity: 1 }
                : { productId: item.productId, quantity: Number(item.quantity) }
            )
          : []
      );
    }
    if (!modalOpen) {
      setBundleProducts([]);
      setBundleProductId("");
      setBundleProductQty("");
    }
  }, [modalOpen, edit, setValue]);

  const [autoGiftProductId, setAutoGiftProductId] = useState("");
  const [autoGiftProductQty, setAutoGiftProductQty] = useState();
  const [autoGiftProducts, setAutoGiftProducts] = useState([]);

  const handleAddAutoGiftProduct = () => {
    if (!autoGiftProductId || !autoGiftProductQty || autoGiftProductQty < 1)
      return;
    if (autoGiftProducts.some((item) => item.productId === autoGiftProductId))
      return;
    setAutoGiftProducts([
      ...autoGiftProducts,
      { productId: autoGiftProductId, quantity: autoGiftProductQty },
    ]);
    setAutoGiftProductId("");
    setAutoGiftProductQty();
  };

  const handleRemoveAutoGiftProduct = (productId) => {
    setAutoGiftProducts(
      autoGiftProducts.filter((item) => item.productId !== productId)
    );
  };

  useEffect(() => {
    if (modalOpen && edit) {
      if (edit.productCategory) {
        setValue("productDiscountCategory", edit.productCategory);
        setValue("productDiscountCategorySubCat", edit.productCategory);
        setSelectedCategory({
          value: edit.productCategory,
          label: categoriesData?.data?.find(
            (cat) => cat._id === edit.productCategory
          )?.name,
        });
      }

      if (edit.productSubCategory) {
        setValue("productDiscountSubCategory", edit.productSubCategory);
      }

      if (edit.buyXgetY) {
        setValue("buyXProduct1", edit.buyXgetY.productIdFirst);
        setValue("buyXProduct1Qty", Number(edit.buyXgetY.quantityFirst));
        setValue("buyXProduct2", edit.buyXgetY.productIdSecond);
        setValue("buyXProduct2Qty", Number(edit.buyXgetY.quantitySecond));
      }

      if (edit.mealPlanProductBundles) {
        if (edit.promoCodeType === "mealPlusStoreItem") {
          setValue(
            "mealPlanBundleDuration",
            edit.mealPlanProductBundles.duration === 0
              ? "ALL"
              : `${edit.mealPlanProductBundles.duration}week`
          );
          setValue(
            "mealPlanBundlePercent",
            edit.mealPlanProductBundles.percent
          );
          setValue(
            "mealPlanBundleProducts",
            edit.mealPlanProductBundles.products
          );
        }
      }

      if (edit.mealPlanAddGift) {
        setValue(
          "autoAddGiftMealPlanDuration",
          edit.mealPlanAddGift.duration === 0
            ? "ALL"
            : `${edit.mealPlanAddGift.duration}week`
        );
        setValue("autoAddGiftQuantity", edit.mealPlanAddGift.quantity);
        setValue("autoAddGiftBundleProducts", edit.mealPlanAddGift.products);
      }

      if (categoriesData?.data) {
        const availableCategoryIds = categoriesData.data.map(
          (item) => item._id
        );
        if (
          edit.productDiscountCategory &&
          !availableCategoryIds.includes(edit.productDiscountCategory)
        ) {
          toast.error("The selected Category is no longer available.");
        }
      }
      if (subCategoriesData?.data && edit.productDiscountSubCategory) {
        const availableSubCategoryIds = subCategoriesData.data.map(
          (item) => item._id
        );
        if (
          !availableSubCategoryIds.includes(edit.productDiscountSubCategory)
        ) {
          toast.error("The selected Subcategory is no longer available.");
        }
      }

      if (!edit.productCategory && edit.productDiscountCategory) {
        setTimeout(() => {
          setSelectedCategory({
            value: edit?.productDiscountCategory,
            label: categoriesData?.data?.find(
              (cat) => cat._id === edit?.productDiscountCategory
            )?.name,
          });
          setValue("productDiscountCategory", edit?.productDiscountCategory);
        }, 100);
      }
    }
  }, [modalOpen, categoriesData, subCategoriesData, edit, setValue]);

  useEffect(() => {
    if (edit && subCategoriesData?.status_code) {
      const subCategoryId =
        edit?.productSubCategory || edit?.productDiscountSubCategory;
      if (subCategoryId) {
        setValue("productDiscountSubCategory", subCategoryId);
      }
    }
  }, [subCategoriesData, edit, setValue]);
  useEffect(() => {
    if (
      modalOpen &&
      edit &&
      edit.mealPlanAddGift &&
      edit.promoCodeType === "autoGiftOnMealPlan"
    ) {
      setAutoGiftProducts(
        Array.isArray(edit.mealPlanAddGift.products)
          ? edit.mealPlanAddGift.products.map((item) => ({
              productId: item.productId,
              quantity: item.quantity,
            }))
          : []
      );
    }
    if (!modalOpen) {
      setAutoGiftProducts([]);
      setAutoGiftProductId("");
      setAutoGiftProductQty();
    }
  }, [modalOpen, edit]);

  // Submit handlers
  const onSubmit = async (data) => {
    // Reset error
    setBundleProductsError("");
    if (isUnique && data.userRestriction && data.userRestriction.length > 0) {
      const selectedCustomerIds = data.userRestriction;
      const availableCustomerIds = userList.map((user) => user.value);
      const unavailableCustomerIds = selectedCustomerIds.filter(
        (id) => !availableCustomerIds.includes(id)
      );

      if (unavailableCustomerIds.length > 0) {
        if (unavailableCustomerIds.length === 1) {
          // Single customer unavailable - find the customer name
          const unavailableCustomer = userList.find(
            (user) => user.value === unavailableCustomerIds[0]
          );
          const customerName = unavailableCustomer
            ? unavailableCustomer.label
            : "Selected customer";
          toast.error(`${customerName} is not available anymore.`);
        } else {
          // Multiple customers unavailable
          toast.error("More than one customer is not available anymore.");
        }
        return;
      }
    }

    // Category availability validation
    if (
      data.promoCodeType === "flatDiscountProductCategory" &&
      data.productDiscountCategory
    ) {
      const availableCategoryIds =
        categoriesData?.data?.map((cat) => cat._id) || [];
      if (!availableCategoryIds.includes(data.productDiscountCategory)) {
        // Find the category name from the original data or use a fallback
        const unavailableCategory = categoriesData?.data?.find(
          (cat) => cat._id === data.productDiscountCategory
        );
        const categoryName = unavailableCategory
          ? unavailableCategory.name
          : "Selected category";
        toast.error(`${categoryName} is not available anymore.`);
        return;
      }
    }

    // Subcategory availability validation
    if (data.promoCodeType === "flatDiscountProductSubCategory") {
      // Check main category
      if (data.productDiscountCategorySubCat) {
        const availableCategoryIds =
          categoriesData?.data?.map((cat) => cat._id) || [];
        if (
          !availableCategoryIds.includes(data.productDiscountCategorySubCat)
        ) {
          const unavailableCategory = categoriesData?.data?.find(
            (cat) => cat._id === data.productDiscountCategorySubCat
          );
          const categoryName = unavailableCategory
            ? unavailableCategory.name
            : "Selected category";
          toast.error(`${categoryName} is not available anymore.`);
          return;
        }
      }

      // Check subcategory
      if (data.productDiscountSubCategory) {
        const availableSubCategoryIds =
          subCategoriesData?.data?.map((cat) => cat._id) || [];
        if (
          !availableSubCategoryIds.includes(data.productDiscountSubCategory)
        ) {
          const unavailableSubCategory = subCategoriesData?.data?.find(
            (cat) => cat._id === data.productDiscountSubCategory
          );
          const subCategoryName = unavailableSubCategory
            ? unavailableSubCategory.name
            : "Selected subcategory";
          toast.error(`${subCategoryName} is not available anymore.`);
          return;
        }
      }
    }

    // Validation for mealPlusStoreItem: must have at least one product
    if (
      data.promoCodeType === "mealPlusStoreItem" &&
      (!bundleProducts || bundleProducts.length === 0)
    ) {
      setBundleProductsError(
        "Please add at least one product and a quantity to the bundle"
      );
      return;
    }

    try {
      const body = {};
      body.description = data?.description;
      body.discountType = data?.discountType;
      body.expireDate = data?.expireDate;
      body.startDate = data?.startDate;
      body.code = data?.code;
      body.discountValue = Number(data.discountValue);
      body.minimumSpent = Number(data.minimumSpent);
      body.maxDiscount = Number(data.maxDiscount);
      body.minDiscount = Number(data.minDiscount);
      body.limit = Number(data.limit);
      body.limitPerUser = Number(data.limitPerUser);
      if (data.promoCodeType) body.promoCodeType = data.promoCodeType;

      // Add userRestriction for Unique promo code type
      if (data.promoCodeType === "Unique" && data.userRestriction) {
        body.userRestriction = data.userRestriction;
      }

      if (data.promoCodeType === "flatDiscountProductCategory") {
        body.productCategory = data.productDiscountCategory;
        delete data.productDiscountCategory;
        delete data.productDiscountSubCategory;
      }

      if (data.promoCodeType === "flatDiscountProductSubCategory") {
        body.productCategory = data.productDiscountCategorySubCat;
        body.productSubCategory = data.productDiscountSubCategory;
        delete body.productDiscountCategorySubCat;
        delete body.productDiscountSubCategory;
      }

      if (data.promoCodeType === "buyXgetY") {
        body.buyXgetY = {
          productIdFirst: data.buyXProduct1,
          quantityFirst: String(data.buyXProduct1Qty),
          productIdSecond: data.buyXProduct2,
          quantitySecond: String(data.buyXProduct2Qty),
        };
        delete body.discountType;
        delete body.discountValue;
        delete body.maxDiscount;
        delete body.minDiscount;
        delete body.minimumSpent;
        delete body.buyXProduct1;
        delete body.buyXProduct1Qty;
        delete body.buyXProduct2;
        delete body.buyXProduct2Qty;
      }

      if (data.promoCodeType === "mealPlusStoreItem") {
        setBundleProductsError("");
        if (!data.mealPlanBundleDuration) {
          // eslint-disable-next-line no-undef
          setError("mealPlanBundleDuration", {
            type: "manual",
            message: "Please select meal plan duration",
          });
          return;
        }
        if (!bundleProducts || bundleProducts.length === 0) {
          setBundleProductsError(
            "Please add at least one product and quantity to the bundle."
          );
          return;
        }
        if (
          bundleProducts.some((item) => !item.quantity || item.quantity < 1)
        ) {
          setBundleProductsError(
            "Each product must have a quantity greater than 0."
          );
          return;
        }
        body.mealPlanProductBundles = {
          duration:
            data.mealPlanBundleDuration === "ALL"
              ? 0
              : Number(data.mealPlanBundleDuration?.replace("week", "")),
          products: bundleProducts.map((item) => ({
            productId: item.productId,
            quantity: JSON.stringify(item.quantity),
          })),
        };
        delete body.mealPlanBundleProducts;
        delete body.minimumSpent;
        delete body.maxDiscount;
        delete body.minDiscount;
      }

      if (data.promoCodeType === "autoGiftOnMealPlan") {
        body.mealPlanAddGift = {
          duration:
            data.autoAddGiftMealPlanDuration === "ALL"
              ? 0
              : data.autoAddGiftMealPlanDuration?.replace("week", ""),
          products: autoGiftProducts.map((item) => ({
            productId: item.productId,
            quantity: JSON.stringify(item.quantity),
          })),
        };
        delete body.autoAddGiftMealPlanDuration;
        delete body.autoAddGiftQuantity;
        delete body.autoAddGiftBundleProducts;
        delete body.discountType;
      }

      if (edit !== null) {
        await updatePromoCode({
          promoCodeId: edit._id,
          updatedData: body,
        });
      } else {
        await addPromoCode(body).unwrap();
      }
      setModalOpen(false);
      toast.success(`PromoCode ${edit ? "updated" : "added"} successfully`);
    } catch (error) {
      console.error("Failed to submit promo code:", error);
    }
  };

  const hideHandler = () => {
    setModalOpen(false);
  };

  return (
    <Modal
      show={modalOpen}
      onHide={hideHandler}
      backdrop={true}
      keyboard={false}
      centered
      size="lg"
    >
      <Modal.Body>
        <div className="mx-auto">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="m-0 fw-bold">{title}</h3>
            <button
              type="button"
              className="btn-close"
              onClick={hideHandler}
              aria-label="Close"
              style={{
                background: "none",
                border: "none",
                fontSize: "1.5rem",
                cursor: "pointer",
                color: "#6c757d",
                padding: "0.25rem",
                lineHeight: "1",
              }}
            >
              <FaTimes />
            </button>
          </div>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col lg="6" md="6" className="mb-2">
                {/* Promo Code Type (Select) */}
                <div className="py-2">
                  <label className="form-label">Promo Code Type</label>
                  <Controller
                    name="promoCodeType"
                    control={control}
                    defaultValue={edit?.promoCodeType || ""} // Use edit value or default
                    rules={{ required: "Please enter promo code type" }} // Validation rule
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={promoCodeTypeOptions}
                        className="basic-single"
                        classNamePrefix="select"
                        placeholder="Select promo code type"
                        onChange={(selectedOption) =>
                          field.onChange(selectedOption?.value)
                        }
                        value={promoCodeTypeOptions.find(
                          (option) => option.value === field.value
                        )}
                      />
                    )}
                  />
                  {errors.promoCodeType && (
                    <span className="text-danger">
                      {errors.promoCodeType.message}
                    </span>
                  )}
                </div>
              </Col>
              {/* Code Field */}
              <Col lg="6" md="6" className="mb-2">
                <div className="py-2">
                  <label className="form-label">Promo Code</label>
                  <input
                    type="text"
                    placeholder="Enter promo code"
                    className="form-control"
                    {...register("code", {
                      required: "Please enter promo code",
                      validate: (value) => {
                        if (/^\s/.test(value)) {
                          return "Promo code cannot start with a space";
                        }
                        return true;
                      },
                    })}
                  />
                  {errors.code && (
                    <span className="text-danger">{errors.code.message}</span>
                  )}
                </div>
              </Col>

              {/* Discount Type (Select) field: hide for autoGiftOnMealPlan and buyXgetY */}
              {selectedPromoCodeType !== "autoGiftOnMealPlan" &&
                selectedPromoCodeType !== "buyXgetY" && (
                  <Col lg="6" md="6" className="mb-2">
                    {/* Discount Type (Select) */}
                    <div className="py-2">
                      <label className="form-label">Discount Type</label>
                      <Controller
                        name="discountType"
                        control={control}
                        defaultValue={edit?.discountType || ""}
                        rules={{ required: "Please enter discount type" }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={discountTypeOptions}
                            className="basic-single"
                            classNamePrefix="select"
                            placeholder="Select discount type..."
                            onChange={(selectedOption) =>
                              field.onChange(selectedOption?.value)
                            }
                            value={discountTypeOptions.find(
                              (option) => option.value === field.value
                            )}
                          />
                        )}
                      />
                      {errors.discountType && (
                        <span className="text-danger">
                          {errors.discountType.message}
                        </span>
                      )}
                    </div>
                  </Col>
                )}

              {selectedPromoCodeType !== "autoGiftOnMealPlan" &&
                selectedPromoCodeType !== "buyXgetY" && (
                  <Col lg="6" md="6" className="mb-2">
                    {/* discountValue Field */}
                    <div className="py-2">
                      <label className="form-label">Discount Value</label>
                      <input
                        // type="number"
                        step={
                          selectedDiscountType === "Flat" ? "0.01" : "0.00001"
                        }
                        placeholder="Enter discount value"
                        className="form-control"
                        {...getNumberInputProps("discountValue")}
                        onInput={(e) => {
                          if (selectedDiscountType === "Flat") {
                            const value = e.target.value;
                            const parts = value.split(".");

                            // Remove multiple decimal points
                            if (parts.length > 2) {
                              e.target.value =
                                parts[0] + "." + parts.slice(1).join("");
                            }

                            // Limit decimal places to 2
                            if (parts[1] && parts[1].length > 2) {
                              e.target.value =
                                parts[0] + "." + parts[1].substring(0, 2);
                            }

                            // Limit digits before decimal to 10
                            if (parts[0] && parts[0].length > 10) {
                              e.target.value =
                                parts[0].substring(0, 10) +
                                (parts[1] ? "." + parts[1] : "");
                            }
                          }
                        }}
                        {...register("discountValue", {
                          required: "Please enter discount value",
                          min: 0,
                          validate: (value) => {
                            if (value === 0 || value === "0") {
                              return "Discount value cannot be zero";
                            }
                            if (selectedDiscountType === "Flat") {
                              const regex = /^\d{1,10}(\.\d{1,2})?$/;
                              if (!regex.test(value)) {
                                return "Flat discount should be up to 10 digits with maximum 2 decimal places";
                              }
                              // Check total length including decimal point
                              const stringValue = value.toString();
                              if (stringValue.length > 13) {
                                // 10 digits + 1 decimal point + 2 decimal places = 13
                                return "Flat discount cannot exceed 10 digits with 2 decimal places";
                              }
                              // Check maximum value
                              if (value > 9999999999.99) {
                                return "Flat discount cannot exceed 9999999999.99";
                              }
                            }
                            return true;
                          },
                        })}
                      />
                      {errors.discountValue && (
                        <span className="text-danger">
                          {errors.discountValue.message}
                        </span>
                      )}
                    </div>
                  </Col>
                )}

              {/* Minimum Spent field: hide for mealPlusStoreItem and buyXgetY */}
              {selectedPromoCodeType !== "mealPlusStoreItem" &&
                selectedPromoCodeType !== "buyXgetY" && (
                  <Col lg="6" md="6" className="mb-2">
                    {/* discountValues Field */}
                    <div className="py-2">
                      <label className="form-label">Minimum Spent</label>
                      <input
                        // type="number"
                        placeholder="Enter minimum spent"
                        className="form-control"
                        {...getNumberInputProps("minimumSpent")}
                        {...register(
                          "minimumSpent",
                          {
                            validate: (value) => {
                              if (value && (value === 0 || value === "0")) {
                                return "Minimum spent cannot be zero";
                              }
                              return true;
                            },
                          }
                          // {
                          //   required: "Please enter minimum spent",
                          //   min: 0,
                          // }
                        )}
                      />
                      {errors.minimumSpent && (
                        <span className="text-danger">
                          {errors.minimumSpent.message}
                        </span>
                      )}
                    </div>
                  </Col>
                )}

              {/* Min Discount Value field: hide for autoGiftOnMealPlan, mealPlusStoreItem, and buyXgetY */}
              {selectedPromoCodeType !== "autoGiftOnMealPlan" &&
                selectedPromoCodeType !== "mealPlusStoreItem" &&
                selectedPromoCodeType !== "buyXgetY" && (
                  <Col lg="6" md="6" className="mb-2">
                    {/* minDiscount Field */}
                    <div className="py-2">
                      <label className="form-label">Min Discount Value</label>
                      <input
                        // type="number"
                        placeholder="Enter min discount value"
                        className="form-control"
                        {...getNumberInputProps("minDiscount")}
                        {...register(
                          "minDiscount",
                          {
                            validate: (value) => {
                              if (value && (value === 0 || value === "0")) {
                                return "Min discount cannot be zero";
                              }
                              return true;
                            },
                          }
                          // {
                          //   required: "Please enter min discount",
                          //   min: 1,
                          // }
                        )}
                      />
                      {errors.minDiscount && (
                        <span className="text-danger">
                          {errors.minDiscount.message}
                        </span>
                      )}
                    </div>
                  </Col>
                )}

              {/* Max Discount Value field: hide for autoGiftOnMealPlan, mealPlusStoreItem, and buyXgetY */}
              {selectedPromoCodeType !== "autoGiftOnMealPlan" &&
                selectedPromoCodeType !== "mealPlusStoreItem" &&
                selectedPromoCodeType !== "buyXgetY" && (
                  <Col lg="6" md="6" className="mb-2">
                    {/* maxDiscount Field */}
                    <div className="py-2">
                      <label className="form-label">Max Discount Value</label>
                      <input
                        // type="number"
                        placeholder="Enter max discount value"
                        className="form-control"
                        {...getNumberInputProps("maxDiscount")}
                        {...register(
                          "maxDiscount",
                          {
                            validate: (value) => {
                              if (value && (value === 0 || value === "0")) {
                                return "Max discount cannot be zero";
                              }
                              return true;
                            },
                          }
                          // {
                          //   required: "Please enter max discount",
                          //   min: 1,
                          // }
                        )}
                      />
                      {errors.maxDiscount && (
                        <span className="text-danger">
                          {errors.maxDiscount.message}
                        </span>
                      )}
                    </div>
                  </Col>
                )}

              <Col lg="6" md="6" className="mb-2">
                {/* Limit Field */}
                <div className="py-2">
                  <label className="form-label">Limit</label>
                  <input
                    // type="number"
                    placeholder="Enter limit"
                    className="form-control"
                    {...getNumberInputProps("limit")}
                    {...register("limit", {
                      required: "Please enter limit",
                      min: 1,
                      validate: (value) => {
                        if (value === 0 || value === "0") {
                          return "Limit cannot be zero";
                        }
                        return true;
                      },
                    })}
                  />
                  {errors.limit && (
                    <span className="text-danger">{errors.limit.message}</span>
                  )}
                </div>
              </Col>

              <Col lg="6" md="6" className="mb-2">
                {/* Limit Per User Field */}
                <div className="py-2">
                  <label className="form-label">Limit Per User</label>
                  <input
                    // type="number"
                    placeholder="Enter limit per user"
                    className="form-control"
                    {...getNumberInputProps("limitPerUser")}
                    {...register("limitPerUser", {
                      required: "Please enter limit per user",
                      min: 1,
                      validate: (value) => {
                        if (value === 0 || value === "0") {
                          return "Limit per user cannot be zero";
                        }
                        return true;
                      },
                    })}
                  />
                  {errors.limitPerUser && (
                    <span className="text-danger">
                      {errors.limitPerUser.message}
                    </span>
                  )}
                </div>
              </Col>
              {/* Conditional fields for Flat Discount On Product Category */}
              {showFlatDiscountOnProductCategoryFields && (
                <div
                  style={{
                    border: "1px solid #D9D9D9",
                    padding: "15px",
                    borderRadius: "8px",
                    marginBottom: "15px",
                  }}
                >
                  <Row>
                    <Col lg="6" md="6" className="mb-2">
                      <div className="py-2">
                        <label className="form-label">Category</label>
                        <Select
                          name="productDiscountCategory"
                          options={
                            categoriesData?.data?.map((cat) => ({
                              value: cat._id,
                              label: cat.name,
                            })) || []
                          }
                          className="basic-multi-select"
                          classNamePrefix="select"
                          value={
                            categoriesData?.data
                              ?.map((cat) => ({
                                value: cat._id,
                                label: cat.name,
                              }))
                              .find(
                                (opt) =>
                                  opt.value === watch("productDiscountCategory")
                              ) || null
                          }
                          onChange={(option) => {
                            if (
                              option &&
                              !categoriesData?.data?.some(
                                (cat) => cat._id === option.value
                              )
                            ) {
                              toast.error(
                                `${option.label} is no longer available.`
                              );
                              setValue("productDiscountCategory", "");
                              setSelectedCategory(null);
                              return;
                            }
                            setValue(
                              "productDiscountCategory",
                              option ? option.value : ""
                            );
                            setSelectedCategory(option);
                            setValue("productDiscountSubCategory", "");
                          }}
                          placeholder={
                            isCategoriesLoading
                              ? "Loading..."
                              : "Select category"
                          }
                        />
                        {errors.productDiscountCategory && (
                          <span className="text-danger">
                            {errors.productDiscountCategory.message}
                          </span>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

              {/* Conditional fields for Flat Discount On Product Subcategory */}
              {showFlatDiscountOnProductSubcategoryFields && (
                <div
                  style={{
                    border: "1px solid #D9D9D9",
                    padding: "15px",
                    borderRadius: "8px",
                    marginBottom: "15px",
                  }}
                >
                  <Row>
                    <Col lg="6" md="6" className="mb-2">
                      <div className="py-2">
                        <label className="form-label">Category</label>
                        <Select
                          name="productDiscountCategorySubCat"
                          options={
                            categoriesData?.data?.map((cat) => ({
                              value: cat._id,
                              label: cat.name,
                            })) || []
                          }
                          className="basic-multi-select"
                          classNamePrefix="select"
                          value={
                            categoriesData?.data
                              ?.map((cat) => ({
                                value: cat._id,
                                label: cat.name,
                              }))
                              .find(
                                (opt) =>
                                  opt.value ===
                                  watch("productDiscountCategorySubCat")
                              ) || null
                          }
                          onChange={(option) => {
                            if (
                              option &&
                              !categoriesData?.data?.some(
                                (cat) => cat._id === option.value
                              )
                            ) {
                              toast.error(
                                `${option.label} is no longer available.`
                              );
                              setValue("productDiscountCategorySubCat", "");
                              setSelectedCategory(null);
                              return;
                            }
                            setValue(
                              "productDiscountCategorySubCat",
                              option ? option.value : ""
                            );
                            setSelectedCategory(option);
                            setValue("productDiscountSubCategory", "");
                          }}
                          placeholder={
                            isCategoriesLoading
                              ? "Loading..."
                              : "Select category"
                          }
                        />
                        {errors.productDiscountCategorySubCat && (
                          <span className="text-danger">
                            {errors.productDiscountCategorySubCat.message}
                          </span>
                        )}
                      </div>
                    </Col>
                    <Col lg="6" md="6" className="mb-2">
                      <div className="py-2">
                        <label className="form-label">Sub Category</label>
                        <Select
                          name="productDiscountSubCategory"
                          options={
                            subCategoriesData?.data?.map((cat) => ({
                              value: cat._id,
                              label: cat.name,
                            })) || []
                          }
                          className="basic-multi-select"
                          classNamePrefix="select"
                          value={
                            subCategoriesData?.data
                              ?.map((cat) => ({
                                value: cat._id,
                                label: cat.name,
                              }))
                              .find(
                                (opt) =>
                                  opt.value ===
                                  watch("productDiscountSubCategory")
                              ) || null
                          }
                          onChange={(option) => {
                            if (
                              option &&
                              !subCategoriesData?.data?.some(
                                (cat) => cat._id === option.value
                              )
                            ) {
                              toast.error(
                                `${option.label} is no longer available.`
                              );
                              setValue("productDiscountSubCategory", "");
                              return;
                            }
                            setValue(
                              "productDiscountSubCategory",
                              option ? option.value : ""
                            );
                          }}
                          placeholder={
                            isSubCategoriesLoading
                              ? "Loading..."
                              : "Select sub category"
                          }
                          isDisabled={!selectedCategory}
                        />
                        {errors.productDiscountSubCategory && (
                          <span className="text-danger">
                            {errors.productDiscountSubCategory.message}
                          </span>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

              {/* Conditional fields for Buy X, Get Y free OR Buy X, Get X free */}
              {showBuyXGetYFreeFields && (
                <div
                  style={{
                    border: "1px solid #D9D9D9",
                    padding: "15px",
                    borderRadius: "8px",
                    marginBottom: "15px",
                  }}
                >
                  <Col lg="6" md="6" className="mb-2">
                    <div className="py-2">
                      <label className="form-label">Product 1</label>
                      <Controller
                        name="buyXProduct1"
                        control={control}
                        rules={{ required: "Please select product 1" }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={productOptions}
                            isLoading={isProductsLoading}
                            className="basic-single"
                            classNamePrefix="select"
                            placeholder="Select product 1"
                            onChange={(selectedOption) =>
                              field.onChange(selectedOption?.value)
                            }
                            value={productOptions.find(
                              (option) => option.value === field.value
                            )}
                          />
                        )}
                      />
                      {errors.buyXProduct1 && (
                        <span className="text-danger">
                          {errors.buyXProduct1.message}
                        </span>
                      )}
                    </div>
                  </Col>
                  <Col lg="6" md="6" className="mb-2">
                    <div className="py-2">
                      <label className="form-label">Product 1 Quantity</label>
                      <input
                        // type="number"
                        className="form-control"
                        placeholder="Enter quantity for product 1"
                        {...getNumberInputProps("buyXProduct1Qty")}
                        {...register("buyXProduct1Qty", {
                          required: "Please enter quantity for product 1",
                          min: 1,
                          validate: (value) => {
                            if (value === 0 || value === "0" || value < 1) {
                              return "Quantity must be greater than 0";
                            }
                            return true;
                          },
                        })}
                      />
                      {errors.buyXProduct1Qty && (
                        <span className="text-danger">
                          {errors.buyXProduct1Qty.message}
                        </span>
                      )}
                    </div>
                  </Col>
                  <Col lg="6" md="6" className="mb-2">
                    <div className="py-2">
                      <label className="form-label">Product 2</label>
                      <Controller
                        name="buyXProduct2"
                        control={control}
                        rules={{ required: "Please select product 2" }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={productOptions}
                            isLoading={isProductsLoading}
                            className="basic-single"
                            classNamePrefix="select"
                            placeholder="Select product 2"
                            onChange={(selectedOption) =>
                              field.onChange(selectedOption?.value)
                            }
                            value={productOptions.find(
                              (option) => option.value === field.value
                            )}
                          />
                        )}
                      />
                      {errors.buyXProduct2 && (
                        <span className="text-danger">
                          {errors.buyXProduct2.message}
                        </span>
                      )}
                    </div>
                  </Col>
                  <Col lg="6" md="6" className="mb-2">
                    <div className="py-2">
                      <label className="form-label">Product 2 Quantity</label>
                      <input
                        // type="number"
                        className="form-control"
                        placeholder="Enter quantity for product 2"
                        {...getNumberInputProps("buyXProduct2Qty")}
                        {...register("buyXProduct2Qty", {
                          required: "Please enter quantity for product 2",
                          min: 1,
                          validate: (value) => {
                            if (value === 0 || value === "0" || value < 1) {
                              return "Quantity must be greater than 0";
                            }
                            return true;
                          },
                        })}
                      />
                      {errors.buyXProduct2Qty && (
                        <span className="text-danger">
                          {errors.buyXProduct2Qty.message}
                        </span>
                      )}
                    </div>
                  </Col>
                </div>
              )}

              {/* Conditional fields for Meal Plan + Store Item Bundles */}
              {showMealPlanStoreItemBundlesFields && (
                <>
                  <Col lg="6" md="6" className="mb-2">
                    <div className="py-2">
                      <label className="form-label">Meal Plan Duration</label>
                      <Controller
                        name="mealPlanBundleDuration"
                        control={control}
                        rules={{
                          required: "Please select meal plan duration",
                        }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={mealPlanDurationOptionsWithAll}
                            isLoading={isSettingsLoading}
                            className="basic-single"
                            classNamePrefix="select"
                            placeholder={
                              isSettingsLoading
                                ? "Loading..."
                                : "Select duration"
                            }
                            onChange={(selectedOption) =>
                              field.onChange(selectedOption?.value)
                            }
                            value={mealPlanDurationOptionsWithAll.find(
                              (option) => option.value === field.value
                            )}
                          />
                        )}
                      />
                      {errors.mealPlanBundleDuration && (
                        <span className="text-danger">
                          {errors.mealPlanBundleDuration.message}
                        </span>
                      )}
                    </div>
                  </Col>
                  {/* Product + Quantity + Add Button in one row */}
                  <Col lg="12" className="mb-2">
                    <div className="py-2">
                      <div className="row g-2 align-items-start">
                        <div className="col-5">
                          <label className="form-label">Product</label>
                          <Select
                            options={productOptions}
                            className="basic-single"
                            classNamePrefix="select"
                            placeholder="Select product"
                            value={
                              productOptions.find(
                                (opt) => opt.value === bundleProductId
                              ) || null
                            }
                            onChange={(option) => {
                              if (addProductsError?.product) {
                                setAddProductsError((prev) => ({
                                  ...prev,
                                  product: "",
                                }));
                              }
                              setBundleProductId(option ? option.value : "");
                            }}
                          />
                          {addProductsError?.product && (
                            <Col lg="12" className="mb-2">
                              <span className="text-danger">
                                {addProductsError?.product}
                              </span>
                            </Col>
                          )}
                        </div>
                        <div className="col-5">
                          <label className="form-label">Quantity</label>
                          <input
                            // type="number"
                            className="form-control"
                            placeholder="Enter quantity"
                            min={1}
                            value={bundleProductQty || ""}
                            onChange={(e) => {
                              if (
                                e.target.value &&
                                addProductsError?.quantity
                              ) {
                                setAddProductsError((prev) => ({
                                  ...prev,
                                  quantity: "",
                                }));
                              }
                              const value = e.target.value
                                ? Number(e.target.value)
                                : "";
                              // if (value === 0 || value < 1) {
                              //   toast.error("Quantity must be greater than 0.");
                              //   return;
                              // }
                              setBundleProductQty(value);
                            }}
                            {...getNumberInputProps("bundleProductQty")}
                          />
                          {addProductsError?.quantity && (
                            <Col lg="12" className="mb-2">
                              <span className="text-danger">
                                {addProductsError?.quantity}
                              </span>
                            </Col>
                          )}
                        </div>
                        <div className="col-2 d-flex align-items-end">
                          <Button
                            type="button"
                            className="commonBtn btn-sm ms-2"
                            style={{
                              minWidth: 0,
                              padding: "0.25rem 0.75rem",
                              marginTop: "33px",
                            }}
                            onClick={handleAddBundleProduct}
                          >
                            Add
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Col>
                  {bundleProductsError && (
                    <Col lg="12" className="mb-2">
                      <span className="text-danger">{bundleProductsError}</span>
                    </Col>
                  )}
                  {/* Show added products as cards */}
                  <Col lg="12" className="mb-2">
                    <div
                      style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}
                    >
                      {bundleProducts.map((item) => {
                        const prod = productOptions.find(
                          (opt) => opt.value === item.productId
                        );
                        return (
                          <div
                            key={item.productId}
                            style={{
                              background: "#fff",
                              border: "1px solid #eee",
                              borderRadius: "8px",
                              padding: "10px 16px",
                              minWidth: "180px",
                              boxShadow: "0 2px 8px #0001",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              position: "relative",
                            }}
                          >
                            <div>
                              <b>Product:</b>{" "}
                              {prod ? prod.label : item.productId}
                            </div>
                            <div>
                              <b>Quantity:</b> {item.quantity}
                            </div>
                            <Button
                              type="button"
                              style={{
                                background: "#ff2222",
                                border: "none",
                                marginTop: 8,
                              }}
                              onClick={() =>
                                handleRemoveBundleProduct(item.productId)
                              }
                            >
                              <FaTrash color="#fff" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </Col>
                </>
              )}

              {/* Conditional fields for Auto-Add Gift on Meal Plan Purchase */}
              {showAutoAddGiftOnMealPlanPurchaseFields && (
                <>
                  <Col lg="6" md="6" className="mb-2">
                    <div className="py-2">
                      <label className="form-label">Meal Plan Duration</label>
                      <Controller
                        name="autoAddGiftMealPlanDuration"
                        control={control}
                        rules={{
                          required: "Please select meal plan duration",
                        }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={mealPlanDurationOptionsWithAll}
                            isLoading={isSettingsLoading}
                            className="basic-single"
                            classNamePrefix="select"
                            placeholder={
                              isSettingsLoading
                                ? "Loading..."
                                : "Select duration"
                            }
                            onChange={(selectedOption) =>
                              field.onChange(selectedOption?.value)
                            }
                            value={mealPlanDurationOptionsWithAll.find(
                              (option) => option.value === field.value
                            )}
                          />
                        )}
                      />
                      {errors.autoAddGiftMealPlanDuration && (
                        <span className="text-danger">
                          {errors.autoAddGiftMealPlanDuration.message}
                        </span>
                      )}
                    </div>
                  </Col>
                  <Col lg="6" md="6" className="mb-2">
                    <div className="py-2">
                      <label className="form-label">Product</label>
                      <Select
                        options={productOptions}
                        className="basic-single"
                        classNamePrefix="select"
                        placeholder="Select product"
                        value={
                          productOptions.find(
                            (opt) => opt.value === autoGiftProductId
                          ) || null
                        }
                        onChange={(option) =>
                          setAutoGiftProductId(option ? option.value : "")
                        }
                      />
                    </div>
                  </Col>
                  <Col lg="6" md="6" className="mb-2">
                    <div className="py-2">
                      <div className="row g-2 align-items-end">
                        <div className="col-7">
                          <label className="form-label">Quantity</label>
                          <input
                            // type="number"
                            className="form-control"
                            placeholder="Enter quantity"
                            min={1}
                            value={autoGiftProductQty || ""}
                            onChange={(e) => {
                              const value = Number(e.target.value);
                              // if (value === 0 || value < 1) {
                              //   toast.error("Quantity must be greater than 0.");
                              //   return;
                              // }
                              setAutoGiftProductQty(value);
                            }}
                            {...getNumberInputProps("autoGiftProductQty")}
                          />
                        </div>
                        <div className="col-5 d-flex align-items-end">
                          <Button
                            type="button"
                            className="commonBtn btn-sm ms-2"
                            style={{ minWidth: 0, padding: "0.25rem 0.75rem" }}
                            onClick={handleAddAutoGiftProduct}
                            disabled={
                              !autoGiftProductId ||
                              !autoGiftProductQty ||
                              autoGiftProductQty < 1
                            }
                          >
                            Add
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Col>
                  {/* Show added products as cards */}
                  <Col lg="12" className="mb-2">
                    <div
                      style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}
                    >
                      {autoGiftProducts.map((item) => {
                        const prod = productOptions.find(
                          (opt) => opt.value === item.productId
                        );
                        return (
                          <div
                            key={item.productId}
                            style={{
                              background: "#fff",
                              border: "1px solid #eee",
                              borderRadius: "8px",
                              padding: "10px 16px",
                              minWidth: "180px",
                              boxShadow: "0 2px 8px #0001",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              position: "relative",
                            }}
                          >
                            <div>
                              <b>Product:</b>{" "}
                              {prod ? prod.label : item.productId}
                            </div>
                            <div>
                              <b>Quantity:</b> {item.quantity}
                            </div>
                            <Button
                              type="button"
                              style={{
                                background: "#ff2222",
                                border: "none",
                                marginTop: 8,
                              }}
                              onClick={() =>
                                handleRemoveAutoGiftProduct(item.productId)
                              }
                            >
                              <FaTrash color="#fff" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </Col>
                </>
              )}

              {isUnique && (
                <Col lg="6" md="6" className="mb-2">
                  <div className="py-2">
                    <label className="form-label">Select Customers</label>
                    <Controller
                      name="userRestriction"
                      control={control}
                      defaultValue={edit?.userRestriction || []}
                      rules={{
                        required: "Please select at least one Customer",
                      }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          isMulti
                          options={userList}
                          className="basic-multi-select"
                          classNamePrefix="select"
                          placeholder="Select Customers"
                          onChange={(selectedOptions) =>
                            field.onChange(
                              selectedOptions
                                ? selectedOptions.map((option) => option.value)
                                : []
                            )
                          }
                          value={userList.filter((option) =>
                            field.value.includes(option.value)
                          )}
                        />
                      )}
                    />
                    {errors.userRestriction && (
                      <span className="text-danger">
                        {errors.userRestriction.message}
                      </span>
                    )}
                  </div>
                </Col>
              )}

              <Col lg="6" md="6" className="mb-2">
                {/* Description Field */}
                <div className="py-2">
                  <label className="form-label">Description</label>
                  <textarea
                    rows={4}
                    placeholder="Enter description"
                    className="form-control"
                    // {...register("description", {
                    //   required: "Please enter description",
                    // })}
                    {...register("description", {
                      required: "Please enter description",
                      maxLength: {
                        value: 500,
                        message: "Description cannot exceed 500 characters",
                      },
                      validate: (value) => {
                        if (!value || value.trim() === "") {
                          return "Description cannot be empty";
                        }
                        if (/^\s/.test(value)) {
                          return "Description cannot start with a space";
                        }
                        // if (/[^a-zA-Z0-9\s]/.test(value)) {
                        //   return "Description cannot include special characters or emojis";
                        // }
                        return true;
                      },
                    })}
                  ></textarea>
                  {errors.description && (
                    <span className="text-danger">
                      {errors.description.message}
                    </span>
                  )}
                </div>
              </Col>

              <Col lg="6" md="6" className="mb-2">
                {/* Start Date Field */}
                <div className="py-2">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    {...register("startDate", {
                      required: "Please enter start date",
                    })}
                  />
                  {errors.startDate && (
                    <span className="text-danger">
                      {errors.startDate.message}
                    </span>
                  )}
                </div>
              </Col>

              <Col lg="6" md="6" className="mb-2">
                {/* Expire Date Field */}
                <div className="py-2">
                  <label className="form-label">Expire Date</label>
                  <input
                    type="date"
                    className="form-control"
                    {...register("expireDate", {
                      required: "Please enter expire date",
                      validate: (value) =>
                        new Date(value) >= new Date().setHours(0, 0, 0, 0) ||
                        "Expire date cannot be in the past",
                    })}
                    min={new Date().toISOString().split("T")[0]} // Disable past dates
                  />
                  {errors.expireDate && (
                    <span className="text-danger">
                      {errors.expireDate.message}
                    </span>
                  )}
                </div>
              </Col>
            </Row>

            {/* Submit & Cancel Buttons */}
            <Col lg="12" className="my-2">
              <div className="d-flex align-items-center justify-content-center gap-10">
                <Button
                  className="d-flex align-items-center justify-content-center commonBtn"
                  type="submit"
                  disabled={isAdding || isUpdating}
                >
                  {isAdding || isUpdating ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Loading...
                    </>
                  ) : (
                    "Submit"
                  )}
                </Button>
                <Button
                  className="d-flex align-items-center justify-content-center commonBtn GreyBtn"
                  type="button"
                  onClick={() => {
                    setModalOpen(false);
                  }}
                  disabled={isAdding || isUpdating}
                >
                  Cancel
                </Button>
              </div>
            </Col>
          </Form>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PromoCodesPop;
